import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SpinnerDefault, TitlelessCard } from "../../components";
import CTEyeService from "../../services/CTEyeService";
import { InspectionService, UrlService, UserService } from "../../services";
import { useParams, useSearchParams } from "react-router-dom";
import { HeartbeatPayloadDetailed, LiveViewSessionResponse, LiveInspectionResponse, PeakSignalModel, PeakSignalUpdateModel } from "../../data/entities";
import { Roles } from "../../models";
import { deriveFlwGaugeConfig, getGaugeLevel, worstGaugeLevel } from "./helper/helpers";
import { useFreshness, useSessionDuration, useGaugeConfig } from "./hooks";
import { POLL_LIVE_VIEW_DATA_MS } from "./helper";
import { BreachBanner, SessionEndedBanner, GaugeCard, NoDataReceivedCard, FilePickerCard, NoActiveStreamCard, LiveViewHeader, AnimatedBanner, PeakSignalTable } from "./components";
import { FlwGaugeData } from "./components/live-view/GaugeCard";

export function CTEyeClientLiveView() {
  const urlParams = useParams();
  const [searchParams] = useSearchParams();
  const id = UrlService.getParamValue("id", urlParams) ?? "0";
  const filePathFromQuery = searchParams.get("filePath");

  const service = new CTEyeService();

  const [uploadSessionId, setUploadSessionId] = useState<string | null>(null);
  const [liveData, setLiveData] = useState<LiveViewSessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [startError, setStartError] = useState(false);
  const [lastDataTimestamp, setLastDataTimestamp] = useState<number | null>(null);

  const lastUploadAtRef = useRef<string | null>(null);
  const startedRef = useRef<boolean>(false);
  const gaugeConfig = useGaugeConfig(id);
  const { secondsAgo } = useFreshness(lastDataTimestamp);
  const sessionElapsed = useSessionDuration(liveData?.startDate ? liveData.startDate : null);

  const [activeFiles, setActiveFiles] = useState<LiveInspectionResponse[]>([]);
  const navigate = useNavigate();
  const [deviceWindowSize, setDeviceWindowSize] = useState<number | null>(null);
  
  useEffect(() => {
    new CTEyeService()
      .getLatestHeartbeat(id)
      .then((hb) => {
        const payload = hb?.payload as unknown as HeartbeatPayloadDetailed | undefined;
        setDeviceWindowSize(payload?.activeConfiguration?.rollingAverageWindowSize ?? null);
      })
      .catch(() => setDeviceWindowSize(null));
  }, [id]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const start = async () => {
      try {
        const liveInspections = await service.getLiveInspections();
        const activeForClient = liveInspections.filter((i: LiveInspectionResponse) => i.clientId === id);

        if (!filePathFromQuery) {
          if (activeForClient.length === 1) {
            startedRef.current = false;
            const params = new URLSearchParams({ filePath: activeForClient[0].filePath });
            navigate(`/cteye/clients/${id}/live-view?${params.toString()}`, { replace: true });
            return;
          }
          if (activeForClient.length > 1) {
            setActiveFiles(activeForClient);
            return;
          }
          setStartError(true);
          return;
        }

        const isFileActive = activeForClient.some((i: LiveInspectionResponse) => i.filePath === filePathFromQuery,);

        if (!isFileActive) {
          setStartError(true);
          navigate(`/cteye/clients/${id}/live-view`, { replace: true });
          return;
        }

        const result = await service.startLiveView({ clientId: id, filePath: filePathFromQuery });
        if (result?.uploadSessionId) {
          setUploadSessionId(result.uploadSessionId);
        } else {
          setStartError(true);
        }
      } catch {
        setStartError(true);
      } finally {
        setLoading(false);
      }
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filePathFromQuery]);

  useEffect(() => {
    if (!uploadSessionId) return;

    const poll = async () => {
      try {
        const data = await service.getLiveViewData(uploadSessionId);
        
        if (!data) return;
        setLiveData(data);

        if (data.status === "Completed" || data.status === "Failed" || data.status === "Cancelled") {
          clearInterval(interval);
          return;
        }

        if (data.lastUploadAt && data.lastUploadAt !== lastUploadAtRef.current) {
          lastUploadAtRef.current = data.lastUploadAt;
          setLastDataTimestamp(Date.now());
        }
      } catch (error) {
        console.error("Error polling live view data:", error);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_LIVE_VIEW_DATA_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSessionId]);

  if (loading) return <SpinnerDefault />;

  const isFailed = liveData?.status === "Failed";
  const isSessionCompleted = liveData?.status === "Completed" || isFailed;
  const isLive = liveData != null && !isSessionCompleted;
  const hasFlawData = liveData?.currentFlawValue != null;
  const config = gaugeConfig;

  const flwConfig = deriveFlwGaugeConfig(config);

  const flw: FlwGaugeData | null = flwConfig && liveData
    ? {
        config: flwConfig,
        isReady: liveData.currentFlwDifference != null,
        currentDifference: liveData.currentFlwDifference,
        peakDifference: liveData.peakFlwDifference,
        peakDifferenceAt: liveData.peakFlwDifferenceAt,
        windowSize: deviceWindowSize,
      }
    : null;

  const flawBreach = getGaugeLevel(liveData?.peakFlawValue, config);
  const flwBreach = getGaugeLevel(liveData?.peakFlwDifference, flwConfig);
  const breachLevel = worstGaugeLevel(flawBreach, flwBreach);
  const breachSource: "flaw" | "deviation" | "both" =
    flawBreach === breachLevel && flwBreach === breachLevel
      ? "both"
      : flwBreach === breachLevel
        ? "deviation"
        : "flaw";

  const showError = (startError || !filePathFromQuery) && !liveData && activeFiles.length === 0;
  const showPicker = activeFiles.length > 0 && !uploadSessionId;
  const showConnecting = !startError && filePathFromQuery != null && !liveData;

  const handleFileSelect = (filePath: string) => {
    startedRef.current = false;
    setActiveFiles([]);
    setLoading(true);
    const params = new URLSearchParams({ filePath });
    navigate(`/cteye/clients/${id}/live-view?${params.toString()}`, { replace: true });
  };

  const canEditPeaks = UserService.isInAnyRole([Roles.admin, Roles.dataAnalyst]);
  const handlePeakUpdate = async (signal: PeakSignalModel, changes: PeakSignalUpdateModel) => {
    await new InspectionService().updatePeakSignal(signal.id, changes);
  };
  const handlePeakDelete = async (signal: PeakSignalModel) => {
    await new InspectionService().deletePeakSignal(signal.id);
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-10 col-xl-8">
        <div className="d-flex flex-column gap-3">
          <LiveViewHeader
            filePathFromQuery={filePathFromQuery}
            uploadSessionId={uploadSessionId}
            isLive={isLive}
            isSessionCompleted={isSessionCompleted}
            sessionElapsed={sessionElapsed}
          />

          {showError && <NoActiveStreamCard startError={startError} />}

          {showPicker && (
            <FilePickerCard activeFiles={activeFiles} onFileSelect={handleFileSelect} />
          )}

          {showConnecting && (
            <TitlelessCard>
              <div className="d-flex flex-column align-items-center py-4 text-muted">
                <SpinnerDefault />
                <small className="mt-2">Connecting to live stream&hellip;</small>
              </div>
            </TitlelessCard>
          )}

          {liveData && (
            <TitlelessCard>
              <AnimatedBanner visible={isSessionCompleted}>
                <SessionEndedBanner isFailed={isFailed} />
              </AnimatedBanner>

              <AnimatedBanner visible={breachLevel !== "none"}>
                <BreachBanner breachLevel={breachLevel as "caution" | "critical"} source={breachSource} />
              </AnimatedBanner>
              
              <div className="d-flex flex-column align-items-center">
                {hasFlawData ? (
                  <GaugeCard
                    currentFlawValue={liveData.currentFlawValue ?? 0}
                    peakFlawValue={liveData.peakFlawValue}
                    peakFlawValueAt={liveData.peakFlawValueAt}
                    config={config}
                    flw={flw}
                    isLive={isLive}
                    lastDataTimestamp={lastDataTimestamp}
                    secondsAgo={secondsAgo}
                  />
                ) : isSessionCompleted ? (
                  <NoDataReceivedCard isFailed={isFailed} errorMessage={liveData.errorMessage ?? undefined} />
                ) : (
                  <div className="d-flex flex-column align-items-center py-4 text-muted">
                    <SpinnerDefault />
                    <small>Awaiting data&hellip;</small>
                  </div>
                )}
              </div>

              {hasFlawData && (
                <div className="border-top mt-4 pt-3">
                  <PeakSignalTable
                    peakSignals={liveData.peakSignals}
                    config={config}
                    isLive={isLive}
                    canEdit={canEditPeaks}
                    onUpdate={handlePeakUpdate}
                    onDelete={handlePeakDelete}
                  />
                </div>
              )}
            </TitlelessCard>
          )}
        </div>
      </div>
    </div>
  );
}